import {ApolloError} from "@apollo/client";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Tooltip,
    Typography
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {DateComponent, DateVariant} from "components/base/DateComponent";
import CommandBar from "components/disw/CommandBar";
import CommandBarButton from "components/disw/CommandBarButton";
import CommandBarContainer from "components/disw/CommandBarContainer";
import ErrorBox from "components/error/ErrorBox";
import DataGridView from "components/grid/DataGridView";
import {RateAgreementStatus} from "model/schema/globalTypes";
import {
    AlertCircleOutline,
    CheckCircleOutline,
    FileExportOutline
} from "mdi-material-ui";
import {useRef, useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {createRoute} from "utils/url";
import {useAllRateAgreements} from "./hooks/useAllRateAgreements";
import {useRateAgreementExport} from "./hooks/useRateAgreementExport";
import {downloadRateAgreementExport} from "./downloadRateAgreementExport";
import RateAgreementStatusIndicator from "./RateAgreementStatusIndicator";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        gap: theme.spacing(1)
    },
    gridRoot: {
        "&.dx-widget .dx-datagrid .dx-datagrid-header-panel": {
            backgroundColor: "#F5F7F7"
        },
        "&.dx-widget .dx-datagrid .dx-toolbar": {
            backgroundColor: "#F5F7F7"
        },
        "&.dx-widget .dx-datagrid .dx-datagrid-headers .dx-datagrid-table .dx-row > td":
            {
                backgroundColor: "#E6E6E6",
                fontWeight: 600,
                fontSize: 12,
                color: "#1E1E1E",
                padding: 0,
                paddingLeft: theme.spacing(1),
                height: 24,
                borderRight: "solid 1px #aaa",
                borderLeft: "solid 1px #aaa"
            },
        "&.dx-widget .dx-datagrid .dx-row-lines > td": {
            border: "none",
            padding: "4px 8px",
            height: 40,
            color: "#464646",
            fontSize: 12,
            fontWeight: 400,
            textAlign: "left !important"
        },
        "&.dx-widget .dx-datagrid .dx-datagrid-rowsview .dx-row": {
            "&:hover": {backgroundColor: "#EEF6F8"}
        }
    },
    link: {
        color: "#0073C6",
        textDecoration: "none",
        "&:hover": {textDecoration: "underline"}
    }
}));

interface RateAgreementRow {
    id: string;
    name: string;
    description: string | null;
    carrier: string;
    serviceMethod: string;
    status: RateAgreementStatus;
    rateTableCount: number;
    lastUpdatedBy: string;
    lastUpdatedAt: Date;
}

export default function RateAgreements() {
    const classes = useStyles();
    const {rateAgreements, loading, error} = useAllRateAgreements();
    const [status, setStatus] = useState<RateAgreementStatus>(
        RateAgreementStatus.Released
    );
    const [exportError, setExportError] = useState<string | null>(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState<string | null>(
        null
    );
    const {createExport} = useRateAgreementExport();
    const [exportCompleted, setExportCompleted] = useState(false);
    const exportInFlight = useRef(false);
    const exportStatusText = exportError
        ? "Export failed"
        : exportCompleted
        ? "Download started"
        : exporting
        ? downloadFileName
            ? "Downloading file..."
            : "Preparing export..."
        : "Ready to export.";

    const rows: RateAgreementRow[] = rateAgreements.map(ra => ({
        id: ra.id,
        name: ra.name,
        description: ra.description ?? null,
        carrier: ra.carrier,
        serviceMethod: ra.serviceMethod,
        status: ra.status,
        rateTableCount: ra.rateTables.length,
        lastUpdatedBy: ra.audit.latestChange.changedBy,
        lastUpdatedAt: ra.audit.latestChange.changedAt
    }));

    if (loading) return <LinearProgress />;
    if (error) return <ErrorBox apolloError={error} />;

    const startExport = async () => {
        if (exportInFlight.current) return;
        exportInFlight.current = true;
        setExporting(true);
        setExportError(null);
        setDownloadFileName(null);
        setExportCompleted(false);
        try {
            const result = await createExport({variables: {status}});
            const exportDetails = result.data?.rates?.createRateAgreementExport;
            if (!exportDetails) {
                throw new Error(
                    "The rates export could not be created. Please try again."
                );
            }
            setDownloadFileName(exportDetails.fileName);
            await downloadRateAgreementExport(exportDetails);
            setExportCompleted(true);
        } catch (exception) {
            const accessDenied =
                exception instanceof ApolloError &&
                exception.graphQLErrors.some(error =>
                    [
                        "FEATURE_NOT_LICENSED",
                        "FORBIDDEN",
                        "UNAUTHENTICATED"
                    ].includes(error.extensions?.code)
                );
            setExportError(
                accessDenied
                    ? "Your Rates access could not be verified. Ask your administrator to check your access, then try again."
                    : "We couldn't download your rates file. Please try again. If the problem continues, contact support."
            );
        } finally {
            exportInFlight.current = false;
            setExporting(false);
        }
    };

    return (
        <CommandBarContainer
            sidebar={
                <CommandBar>
                    <CommandBarButton
                        icon={
                            <FileExportOutline titleAccess="Export rate agreements" />
                        }
                        label="Export"
                        tooltip="Export rate agreements"
                        onClick={() => {
                            setExportError(null);
                            setDownloadFileName(null);
                            setExportCompleted(false);
                            setStatus(RateAgreementStatus.Released);
                            setExportDialogOpen(true);
                        }}
                        disabled={exporting}
                    />
                </CommandBar>
            }
            main={
                <div className={classes.root}>
                    <Dialog
                        open={exportDialogOpen}
                        onClose={
                            exporting
                                ? undefined
                                : () => setExportDialogOpen(false)
                        }
                        aria-labelledby="rates-export-title"
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle id="rates-export-title">
                            Export rate agreements
                        </DialogTitle>
                        <DialogContent sx={{pb: 0}}>
                            <DialogContentText>
                                Download all rate agreements with the selected
                                status as an SCS tableset file.
                            </DialogContentText>
                            <FormControl
                                fullWidth
                                margin="normal"
                                disabled={exporting}
                            >
                                <InputLabel id="rates-export-status-label">
                                    Agreement status
                                </InputLabel>
                                <Select
                                    labelId="rates-export-status-label"
                                    id="rates-export-status"
                                    label="Agreement status"
                                    value={status}
                                    onChange={event => {
                                        setStatus(
                                            event.target
                                                .value as RateAgreementStatus
                                        );
                                        setExportError(null);
                                        setDownloadFileName(null);
                                        setExportCompleted(false);
                                    }}
                                >
                                    <MenuItem
                                        value={RateAgreementStatus.Released}
                                    >
                                        Released
                                    </MenuItem>
                                    <MenuItem value={RateAgreementStatus.Draft}>
                                        Draft
                                    </MenuItem>
                                    <MenuItem
                                        value={RateAgreementStatus.Archived}
                                    >
                                        Archived
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </DialogContent>
                        <DialogActions sx={{px: 3, pb: 2}}>
                            {!exportCompleted && (
                                <Button
                                    onClick={() => setExportDialogOpen(false)}
                                    disabled={exporting}
                                    sx={{width: 80}}
                                >
                                    {exportError ? "Close" : "Cancel"}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={
                                    exportCompleted
                                        ? () => setExportDialogOpen(false)
                                        : startExport
                                }
                                disabled={exporting}
                                aria-busy={exporting}
                                sx={{width: 170}}
                            >
                                {exportCompleted
                                    ? "Close"
                                    : exportError
                                    ? "Try again"
                                    : "Export Rates"}
                            </Button>
                        </DialogActions>
                        <Box
                            sx={{
                                height: 176,
                                flexShrink: 0,
                                overflowY: "auto",
                                borderTop: 1,
                                borderColor: "divider",
                                px: 3,
                                py: 2,
                                overflowWrap: "anywhere"
                            }}
                        >
                            <Typography
                                component="h3"
                                id="rates-export-details-title"
                                variant="subtitle2"
                                sx={{mb: 1.5}}
                            >
                                Export Details
                            </Typography>
                            <Box
                                component="dl"
                                role="status"
                                aria-labelledby="rates-export-details-title"
                                aria-live="polite"
                                aria-atomic="true"
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "auto 24px minmax(0, 1fr)",
                                    columnGap: 1,
                                    rowGap: 1.5,
                                    m: 0,
                                    alignItems: "start"
                                }}
                            >
                                <Typography
                                    component="dt"
                                    variant="body2"
                                    fontWeight={600}
                                >
                                    Exported File:
                                </Typography>
                                <Typography
                                    component="dd"
                                    variant="body2"
                                    title={downloadFileName ?? undefined}
                                    sx={{
                                        gridColumn: 3,
                                        m: 0,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}
                                >
                                    {downloadFileName ??
                                        `All ${status.toLowerCase()} rate agreements`}
                                </Typography>
                                <Typography
                                    component="dt"
                                    variant="body2"
                                    fontWeight={600}
                                >
                                    Export Status:
                                </Typography>
                                <Box
                                    component="dd"
                                    sx={{
                                        m: 0,
                                        minWidth: 0,
                                        gridColumn: "2 / 4"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "24px minmax(0, 1fr)",
                                            columnGap: 1,
                                            rowGap: 1,
                                            alignItems: "start"
                                        }}
                                    >
                                        {exportError ||
                                        exportCompleted ||
                                        exporting ? (
                                            <Tooltip
                                                describeChild
                                                title={
                                                    exportError
                                                        ? "The export failed. Read the recovery advice beside this indicator."
                                                        : exportCompleted
                                                        ? "The file was handed to your browser. Check your downloads to save or open it."
                                                        : "The export is in progress. Keep this window open."
                                                }
                                            >
                                                <Box
                                                    component="span"
                                                    tabIndex={0}
                                                    aria-label={
                                                        exportStatusText
                                                    }
                                                    sx={{
                                                        display: "inline-flex",
                                                        width: 24,
                                                        height: 24
                                                    }}
                                                >
                                                    {exportError ? (
                                                        <AlertCircleOutline
                                                            color="error"
                                                            titleAccess="Export failed"
                                                            sx={{flexShrink: 0}}
                                                        />
                                                    ) : exportCompleted ? (
                                                        <CheckCircleOutline
                                                            color="success"
                                                            titleAccess="Download started"
                                                            sx={{flexShrink: 0}}
                                                        />
                                                    ) : (
                                                        <CircularProgress
                                                            size={24}
                                                            sx={{flexShrink: 0}}
                                                            aria-label="Export in progress"
                                                        />
                                                    )}
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Box
                                                component="span"
                                                aria-hidden="true"
                                                sx={{width: 24, height: 24}}
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={
                                                exportError
                                                    ? undefined
                                                    : {
                                                          whiteSpace: "nowrap",
                                                          overflow: "hidden",
                                                          textOverflow:
                                                              "ellipsis"
                                                      }
                                            }
                                            title={exportStatusText}
                                        >
                                            {exportStatusText}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            role={
                                                exportError
                                                    ? "alert"
                                                    : undefined
                                            }
                                            color="text.secondary"
                                            sx={{gridColumn: 2}}
                                        >
                                            {exportError
                                                ? exportError
                                                : exportCompleted
                                                ? "Find the file in your browser's downloads."
                                                : exporting
                                                ? "Keep this window open."
                                                : "Includes all agreements and rates for this status, regardless of viewer filters."}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Dialog>
                    <DataGridView<RateAgreementRow>
                        dataSource={rows}
                        isLoading={false}
                        showFilterRow={true}
                        allowSelection={false}
                        height="100%"
                        gridClasses={{root: classes.gridRoot}}
                        columns={[
                            {
                                colDef: {
                                    dataField: "name",
                                    caption: "Name",
                                    allowSorting: true
                                },
                                render: ({data}) => (
                                    <RouterLink
                                        to={createRoute(
                                            `/rate-agreements/${data.id}`
                                        )}
                                        className={classes.link}
                                    >
                                        {data.name}
                                    </RouterLink>
                                )
                            },
                            {
                                colDef: {
                                    dataField: "description",
                                    caption: "Description",
                                    allowSorting: false,
                                    width: 300
                                }
                            },
                            {
                                colDef: {
                                    dataField: "carrier",
                                    caption: "Carrier",
                                    allowSorting: true
                                }
                            },
                            {
                                colDef: {
                                    dataField: "serviceMethod",
                                    caption: "Service Method",
                                    allowSorting: true
                                }
                            },
                            {
                                colDef: {
                                    dataField: "status",
                                    caption: "Status",
                                    allowSorting: true,
                                    width: 120
                                },
                                render: ({data}) => (
                                    <RateAgreementStatusIndicator
                                        status={data.status}
                                        data-testid={`status-${data.id}`}
                                    />
                                )
                            },
                            {
                                colDef: {
                                    dataField: "rateTableCount",
                                    caption: "# Rate Tables",
                                    allowSorting: true,
                                    alignment: "right",
                                    width: 120
                                }
                            },
                            {
                                colDef: {
                                    dataField: "lastUpdatedBy",
                                    caption: "Last Updated By",
                                    allowSorting: true
                                }
                            },
                            {
                                colDef: {
                                    dataField: "lastUpdatedAt",
                                    caption: "Last Updated At",
                                    allowSorting: true,
                                    width: 180
                                },
                                render: ({data}) => (
                                    <span data-testid={`updated-at-${data.id}`}>
                                        <DateComponent
                                            date={data.lastUpdatedAt}
                                            variant={
                                                DateVariant.SHORT_WITH_TIME
                                            }
                                        />
                                    </span>
                                )
                            }
                        ]}
                        data-testid="rate-agreements-grid"
                    />
                </div>
            }
        />
    );
}
